import { Injectable, signal } from '@angular/core';

/** A failed HTTP call, recorded as metadata only. Never bodies. */
export interface IFailedRequestRecord {
  correlationId: string;
  method: string;
  /** Path with ids and query string stripped. */
  maskedUrl: string;
  status: number;
  at: string;
}

export interface IClientErrorRecord {
  message: string;
  at: string;
}

/** The snapshot attached to a ticket at capture time. */
export interface ITicketContext {
  correlationIds: string[];
  failedRequests: IFailedRequestRecord[];
  clientErrors: IClientErrorRecord[];
  routePath?: string;
  appVersion?: string;
  releasedAt?: string;
  roles?: string[];
  tenant?: string;
  companyId?: string;
  locationId?: string;
  browser?: string;
  os?: string;
  viewport?: string;
  locale?: string;
  capturedAt: string;
}

const MAX_FAILED_REQUESTS = 50;
const MAX_CLIENT_ERRORS = 20;
const SNAPSHOT_WINDOW_MS = 2 * 60 * 1000;
const MAX_ERROR_MESSAGE_LENGTH = 500;

/**
 * `navigator.platform` is deprecated. `userAgentData.platform` is the
 * standards-track replacement but ships only in Chromium, so this prefers it
 * and falls back.
 */
function platformLabel(): string | undefined {
  const data = (
    navigator as Navigator & { userAgentData?: { platform?: string } }
  ).userAgentData;
  return data?.platform ?? navigator.platform;
}

/**
 * Three bounded in-memory buffers feeding the support ticket's `context` blob.
 *
 * Two rules hold everywhere in this file, because this is a HIS and the buffers
 * outlive the screen the user was on:
 *
 *  1. **Never `localStorage`.** These buffers die with the tab. Persisting them
 *     would leave diagnostic traces of clinical sessions on shared workstations.
 *  2. **Sanitise at write time, not at snapshot time.** A record is scrubbed
 *     before it enters the buffer, so PHI never sits in memory even transiently
 *     and a later change to the snapshot path cannot re-expose it.
 */
@Injectable({ providedIn: 'root' })
export class ClientDiagnosticsService {
  private readonly failedRequests = signal<IFailedRequestRecord[]>([]);
  private readonly clientErrors = signal<IClientErrorRecord[]>([]);
  private readonly routePath = signal<string | undefined>(undefined);
  private readonly appVersion = signal<string | undefined>(undefined);
  private readonly releasedAt = signal<string | undefined>(undefined);

  /** Read-only views, for a diagnostics panel or debugging. */
  readonly failedRequests$ = this.failedRequests.asReadonly();
  readonly clientErrors$ = this.clientErrors.asReadonly();
  readonly currentRoutePath = this.routePath.asReadonly();

  recordFailedRequest(record: IFailedRequestRecord): void {
    this.failedRequests.update((records) =>
      [...records, record].slice(-MAX_FAILED_REQUESTS),
    );
  }

  recordClientError(message: string): void {
    this.clientErrors.update((records) =>
      [
        ...records,
        {
          message: ClientDiagnosticsService.sanitise(message),
          at: new Date().toISOString(),
        },
      ].slice(-MAX_CLIENT_ERRORS),
    );
  }

  /**
   * The route *configuration* path (`/oncology/patients/:id/protocol/:protocolId`),
   * not the resolved URL. Masking by construction: a config path cannot contain
   * a patient id, so there is nothing to strip.
   */
  setRoutePath(configPath: string | undefined): void {
    this.routePath.set(configPath);
  }

  setAppVersion(version: string | undefined, releasedAt?: string): void {
    this.appVersion.set(version);
    this.releasedAt.set(releasedAt);
  }

  snapshot(extra: Partial<ITicketContext> = {}): ITicketContext {
    const cutoff = Date.now() - SNAPSHOT_WINDOW_MS;
    const recentFailures = this.failedRequests().filter(
      (record) => new Date(record.at).getTime() >= cutoff,
    );
    // Windowed on the same cutoff as the failed requests. Attaching the whole
    // buffer meant a ticket filed at 5pm could carry a 9am error — noise the
    // reporter never saw and support would have to rule out by hand.
    const recentErrors = this.clientErrors().filter(
      (record) => new Date(record.at).getTime() >= cutoff,
    );

    return {
      correlationIds: recentFailures.map((record) => record.correlationId),
      failedRequests: recentFailures,
      clientErrors: recentErrors,
      routePath: this.routePath(),
      appVersion: this.appVersion(),
      releasedAt: this.releasedAt(),
      browser: navigator.userAgent,
      // `navigator.platform` is deprecated; `userAgentData` is the replacement
      // but is Chromium-only, so it is used when present and the deprecated
      // field remains the fallback.
      os: platformLabel(),
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      locale: navigator.language,
      capturedAt: new Date().toISOString(),
      ...extra,
    };
  }

  /**
   * Clears every buffer.
   *
   * Not called anywhere today: logout is a full-page redirect to Keycloak, so
   * the whole service is torn down with the document. It exists for the case
   * that stops being true — an SPA-internal logout would otherwise leave one
   * user's captured errors in memory for the next person at a shared
   * workstation.
   */
  reset(): void {
    this.failedRequests.set([]);
    this.clientErrors.set([]);
    this.routePath.set(undefined);
  }

  /**
   * Truncate, drop query strings from URLs inside stack traces, and remove
   * anything shaped like a Romanian CNP or a medical record number. Applied
   * before the message reaches the buffer.
   */
  static sanitise(message: string): string {
    return message
      .slice(0, MAX_ERROR_MESSAGE_LENGTH)
      .replace(/\?[^\s)'"]*/g, '')
      .replace(/\b\d{13}\b/g, '[REDACTED]')
      .replace(/\b(?:MRN|CID|FO|CNP)[\s:._-]*\d{4,12}\b/gi, '[REDACTED]');
  }

  /**
   * Strip the query string and replace path segments that look like ids (uuid
   * or long numeric) with `:id`, so a failed-request record cannot carry a
   * patient identifier.
   */
  static maskUrl(url: string): string {
    const withoutQuery = url.split('?')[0];
    return withoutQuery
      .split('/')
      .map((segment) =>
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          segment,
        ) || /^\d{4,}$/.test(segment)
          ? ':id'
          : segment,
      )
      .join('/');
  }
}
